// =====================================================================
// commande-prete/fiches.js — Commande prête Nespart (13-10-2026)
// =====================================================================
// Logique d'écran du PDA, reprise de la maquette validée par Silviu
// (flux-commande-prete.html : ssccProbleme, stateOf, aFaire, allerA, avancer,
// onEnter, setCount, poserRef…). Fonctions PURES : elles reçoivent l'état
// `st` de l'écran au lieu de lire le DOM, et rendent des identifiants de
// champ (« sscc-<id> », « h-<id> », « readyNote »…) que la page focalise.
//
// `st` = { units: [fiche], tic, format, fl, fw, ord, refusEntete? }
//   ord = la réf. de chargement (ODP), SEUL identifiant de la commande (14/10 ; champ #ord)
// fiche = { id, type: 'PAL'|'COL', sscc, ssccAt, ref?, valide?, h, l, w, kg, photos, refus? }
//   (valeurs TAPÉES, en texte : le message de saisie cite ce qui a été tapé ;
//    refus = { champ: message } — valeur juste pour le PDA mais REFUSÉE par le
//    serveur, ex. « SSCC déjà dans la commande X » : traitée comme une valeur fausse)
//
// Différences voulues avec la maquette (Silviu, 13/10 + défauts relevés sur la maquette) :
//   - la photo est OBLIGATOIRE — une fiche sans photo n'est pas « complète » (manque : photo) ;
//   - quand plus rien n'est à saisir, Entrée/scan (et le bouton d'envoi) mènent à une fiche à
//     laquelle il ne manque que la photo (message « photo-msg-<id> » + bouton 📷 mis en évidence) ;
//   - un SSCC en double est signalé sur les DEUX fiches (aucune ne reste « complète ✓ ») ;
//     seule la fiche scannée en dernier reste « à faire » pour la navigation.
// =====================================================================

import { normSscc, ssccValide, motifSsccInvalide, avertEtiquette, abregeSscc } from './sscc.js';
import {
  champs, verifChamp, formatEtat, isEmpty, num, fmtNum, NOMS, MAX_UNITS, GROS_AJOUT,
  verifRefChargement,
} from './regles.js';
import { MSG_REF_TEST_ADMIN, texteClientManqueTest } from './clients.js';

// Note sous le bouton d'envoi : cible du focus quand tout est complet (Entrée n'y fait rien).
export const ID_NOTE = 'readyNote';
// Message « il ne manque que la photo » d'une fiche (cible du focus, jamais un bouton).
export const idPhoto = (u) => 'photo-msg-' + u.id;

export const ofType = (st, t) => st.units.filter((u) => u.type === t);
export { abregeSscc };   // (défini dans sscc.js — formatage d'un SSCC, aussi utilisé par la page publique)
export const frName = (st, u) => `${u.type === 'PAL' ? 'Palette' : 'Colis'} ${ofType(st, u.type).indexOf(u) + 1}`;
export const enName = (st, u) => `${u.type === 'PAL' ? 'Pallet' : 'Parcel'} ${ofType(st, u.type).indexOf(u) + 1}`;

// ---------- SSCC de RÉFÉRENCE d'une fiche ----------
// Le premier SSCC valide et unique validé (Entrée, Scan, sortie du champ), ou celui confirmé
// par « Oui, garder ». Si la fiche porte ensuite un AUTRE SSCC valide (fiche reprise par
// erreur), Zeus pose la question ; « Non, remettre » rend toujours la référence, quel que soit
// le nombre de scans intermédiaires. Un doublon ou un code illisible ne devient jamais
// référence : corriger un doublon ne déclenche pas de question.
// Deux fiches avec la même référence : seule la PLUS ANCIENNE compte (l'autre n'en a pas).
export const refValide = (st, u) => !!u.ref
  && !st.units.some((x) => x !== u && x.ref && x.ref.sscc === u.ref.sscc && x.ref.at < u.ref.at);

// Rescanner l'étiquette de référence de la fiche ne la rend pas « plus récente » : elle garde
// son ancienneté (sinon le contrôle d'un doublon basculerait l'erreur sur la bonne fiche).
export function setSscc(st, u, v) {
  u.sscc = v;
  u.ssccAt = refValide(st, u) && normSscc(v) === u.ref.sscc ? u.ref.at : ++st.tic;
}

// Problème du SSCC lui-même (illisible, clé fausse, ni GS1 ni étiquette EffiTrace, doublon) ;
// null si rien à redire. Doublons comparés sur la forme normalisée (majuscules).
export function ssccProbleme(st, u) {
  const sscc = normSscc(u.sscc);
  if (!sscc) return null;
  const motif = motifSsccInvalide(sscc);
  if (motif) return motif;
  // Doublon : la fiche dont c'est la référence est la bonne ; sinon, signalé sur la fiche scannée
  // EN DERNIER, sans l'accuser : l'erreur peut être sur l'autre.
  const legit = (x) => refValide(st, x) && x.ref.sscc === sscc;
  const orig = st.units.find((x) => x !== u && normSscc(x.sscc) === sscc
    && (legit(x) !== legit(u) ? legit(x) : x.ssccAt < u.ssccAt));
  if (orig) return `Même SSCC que ${frName(st, orig)} — une étiquette a été scannée deux fois : vérifiez les deux fiches`;
  return null;
}

// Un SSCC refusé par le serveur (u.refus.sscc) ne devient jamais la référence non plus.
export function poserRef(st, u) {
  if (!refValide(st, u) && !ssccProbleme(st, u) && !(u.refus && u.refus.sscc) && ssccValide(normSscc(u.sscc))) u.ref = { sscc: normSscc(u.sscc), at: u.ssccAt };
}
// Une fiche validée dont le SSCC était un doublon reçoit sa référence dès que le doublon est
// réglé sur l'AUTRE fiche (corrigée ou supprimée) : la page rappelle poserRef à chaque rendu.
export function valider(st, u) { u.valide = true; poserRef(st, u); }
// Un SSCC refusé par le serveur (déjà dans une autre commande) ne se « garde » pas : pas de question.
export const enAttente = (st, u) => refValide(st, u) && !ssccProbleme(st, u) && !(u.refus && u.refus.sscc)
  && ssccValide(normSscc(u.sscc)) && normSscc(u.sscc) !== u.ref.sscc;

// Doublon vu depuis la fiche « d'origine » (sa référence, ou scannée en premier) : l'autre
// fiche porte l'erreur, mais celle-ci est AUSSI signalée — sans accuser personne, l'erreur
// peut être sur l'une ou l'autre. Rend l'autre fiche, ou null.
export function doublonDe(st, u) {
  const sscc = normSscc(u.sscc);
  if (!ssccValide(sscc) || ssccProbleme(st, u)) return null;
  return st.units.find((x) => x !== u && normSscc(x.sscc) === sscc) || null;
}

// Message d'une mesure : celui du PDA d'abord ; sinon un refus du serveur pour cette valeur.
export function verifMesure(u, k) {
  const r = verifChamp(u, k);
  if (r.msg || r.manque || !u.refus || !u.refus[k]) return r;
  return { msg: u.refus[k] };
}

// ---------- État d'une fiche ----------
export function stateOf(st, u) {
  const sscc = normSscc(u.sscc);
  const bads = champs(u).map((k) => [k, verifMesure(u, k)]).filter(([, r]) => r.msg);
  const badKeys = bads.map(([k]) => k);
  const enPlus = bads.length ? ' · à vérifier : ' + bads.map(([, r]) => r.msg).join(' · ') : '';
  const p = ssccProbleme(st, u) || (sscc && u.refus && u.refus.sscc) || null;
  if (p) return { cls: 'bad', txt: p + enPlus, ssccBad: true, badKeys };
  const autre = doublonDe(st, u);
  if (autre) {
    return {
      cls: 'bad', txt: `Même SSCC que ${frName(st, autre)} — une étiquette a été scannée deux fois : vérifiez les deux fiches` + enPlus,
      ssccBad: true, doublonOrigine: true, badKeys,
    };
  }
  // SSCC changé sur une fiche qui en avait déjà un : jamais « complète » tant qu'on n'a pas répondu.
  if (enAttente(st, u)) return { cls: bads.length ? 'bad' : 'todo', txt: `SSCC changé (${abregeSscc(u.ref.sscc)} → ${abregeSscc(sscc)}) : répondez à la question de la fiche` + enPlus, attente: true, badKeys };
  if (bads.length) return { cls: 'bad', txt: 'à vérifier : ' + bads.map(([, r]) => r.msg).join(' · '), badKeys };
  const manque = [];
  if (!sscc) manque.push('SSCC');
  champs(u).forEach((k) => { if (verifChamp(u, k).manque) manque.push(NOMS[k]); });
  if (u.type === 'PAL' && !formatEtat(st).ok) manque.push('format palette');
  if (!u.photos) manque.push('photo');   // photo OBLIGATOIRE (Silviu, 13/10)
  if (manque.length) return { cls: 'todo', txt: 'manque : ' + manque.join(', ') };
  return { cls: 'done', txt: `complète ✓ · ${u.photos} photo${u.photos > 1 ? 's' : ''}` };
}
export const complete = (st, u) => stateOf(st, u).cls === 'done';

// Avertissement NON bloquant sous le SSCC : étiquette EffiTrace d'un autre type que la fiche
// (« PAL » sur un colis, sans « PAL » sur une palette). Seulement pour un SSCC lisible et unique.
export const avertFiche = (st, u) => (normSscc(u.sscc) && !ssccProbleme(st, u) ? avertEtiquette(u.sscc, u.type) : null);

// ---------- Suppression d'une fiche ----------
// Le serveur ne supprime une unité qui a des données que sur confirmation explicite (?confirme=1) :
// seulement pour le ✕ CONFIRMÉ (« Supprimer Palette N ») d'une fiche que l'écran montre remplie.
// « − » et ✕ d'une fiche qui paraît vide : jamais (un autre appareil a pu la remplir).
export const optionsSuppression = (u, confirmee) => ({ confirme: !!confirmee && !isEmpty(u) });
// Refus 409 UNITE_REMPLIE : la fiche a été remplie ailleurs entre-temps.
export const msgRempliAilleurs = (type) => (type === 'PAL'
  ? 'Cette palette a été remplie sur un autre appareil : vérifiez avant de la supprimer'
  : 'Ce colis a été rempli sur un autre appareil : vérifiez avant de le supprimer');

// ---------- Navigation Entrée / scan ----------
// Champs encore à faire d'une fiche, dans l'ordre de saisie (vide, faux ou doublon). La photo
// n'en fait pas partie : un scan ne doit jamais atterrir sur un bouton.
export const idsFiche = (u) => ['sscc-' + u.id, ...champs(u).map((k) => `${k}-${u.id}`)];
// SSCC « à refaire » sur CETTE fiche : illisible, clé fausse, refusé, ou doublon scanné en
// dernier. La fiche d'origine d'un doublon est signalée mais n'est pas une cible : le scan
// suivant n'écrase jamais la bonne étiquette.
const ssccAFaire = (st, u) => { const s = stateOf(st, u); return (!!s.ssccBad && !s.doublonOrigine) || !normSscc(u.sscc); };
export function aFaire(st, u) {
  if (enAttente(st, u)) return ['rempl-txt-' + u.id];   // d'abord la question de la fiche
  const ids = [];
  if (ssccAFaire(st, u)) ids.push('sscc-' + u.id);
  for (const k of champs(u)) { const r = verifMesure(u, k); if (r.manque || r.msg) ids.push(`${k}-${u.id}`); }
  return ids;
}
// Rien à saisir, mais pas encore de photo (photo OBLIGATOIRE) : la fiche attend son 📷.
export const manquePhoto = (st, u) => !(u.photos > 0) && !aFaire(st, u).length;

// Premier champ à faire dans la liste de fiches donnée ; plus rien à saisir → le message
// « photo » de la première fiche sans photo ; tout est complet → la note du bouton (Entrée
// n'y fait rien : un scan de trop ne peut ni écraser une fiche ni envoyer le mail).
// Les saisies passent avant les photos : un scan d'étiquette n'est jamais perdu sur un message.
export function cibleAllerA(st, fiches) {
  const v = fiches.find((x) => aFaire(st, x).length);
  if (v) return aFaire(st, v)[0];
  const p = fiches.find((x) => manquePhoto(st, x));
  return p ? idPhoto(p) : ID_NOTE;
}
// Entrée (ou fin de scan) : champ suivant À FAIRE de la fiche, sinon la prochaine fiche
// incomplète. Une fiche complète n'est jamais traversée : le scan suivant ne l'écrase pas.
// `fiche: true` = la cible est dans la fiche en cours (focus sans défilement forcé).
export function cibleAvancer(st, u, fromId) {
  if (enAttente(st, u)) return { id: 'rempl-txt-' + u.id, fiche: true };
  const ids = idsFiche(u), pos = ids.indexOf(fromId);
  // La fiche en cours est terminée d'abord (y compris un champ sauté plus haut) : la mesure
  // suivante n'atterrit jamais sur une autre palette.
  const af = aFaire(st, u);
  const reste = af.find((id) => ids.indexOf(id) > pos) || af.find((id) => id !== fromId);
  if (reste) return { id: reste, fiche: true };
  const k = st.units.indexOf(u);
  const tour = [...st.units.slice(k + 1), ...st.units.slice(0, k), u];
  const v = tour.find((x) => aFaire(st, x).length);
  if (v) return { id: aFaire(st, v)[0], fiche: false };
  // Plus rien à saisir nulle part : la photo de la fiche qu'on vient de finir d'abord
  // (l'opérateur est devant elle), puis les autres.
  if (manquePhoto(st, u)) return { id: idPhoto(u), fiche: true };
  return { id: cibleAllerA(st, tour), fiche: false };
}
// Entrée sur un champ : valeur fausse OU champ vide → on reste dessus (sélectionné). Une Entrée
// de trop (double appui, lecteur en CR+LF) ne saute jamais un champ — sinon hauteur et poids
// s'inverseraient sans alerte. À appeler APRÈS valider() pour le SSCC.
export function entreeFausse(st, u, k) {
  if (k === 'sscc') return ssccAFaire(st, u);
  const r = verifMesure(u, k);
  return !!(r.msg || r.manque);
}

// ---------- Ce qui empêche l'envoi ----------
// Refus du serveur sur l'en-tête (ex. « La réf. X a déjà sa commande ») : { ord?, fmt? }.
const refusEntete = (st) => Object.values(st.refusEntete || {}).filter(Boolean);
export function blockers(st) {
  const b = [];
  const mr = verifRefChargement(st.ord); if (mr) b.push(mr);
  const f = formatEtat(st); if (ofType(st, 'PAL').length && !f.ok) b.push(f.msg);
  b.push(...refusEntete(st));
  const att = [];
  st.units.filter((u) => !complete(st, u)).forEach((u) => (enAttente(st, u) ? att : b).push(`${frName(st, u)} : ${stateOf(st, u).txt}`));
  return [...att, ...b];   // une question en attente passe avant tout le reste
}
// Ce qui bloque à cause de la RÉF. de chargement seule : réf. manquante / invalide, refus du serveur sur la réf.
export const bloqueursRef = (st) => [verifRefChargement(st.ord), (st.refusEntete || {}).ord].filter(Boolean);
// (revue 17/10, 12e passe) La liste `b` de blockers(st) SANS les bloqueurs de la réf. — un écran où elle est figée
// (commande de test, sans le droit) les dit à part (MSG_REF_TEST_ADMIN), jamais comme « à compléter ». Calculée
// depuis `b` déjà là (pas un 2e blockers) : une occurrence retirée par bloqueur de la réf., l'ordre est gardé.
export function sansBloqueursRef(st, b) {
  const ref = bloqueursRef(st);
  return b.filter((x) => { const i = ref.indexOf(x); if (i < 0) return true; ref.splice(i, 1); return false; });
}
// Liste courte d'une note : « <préfixe> — a · b · c · et N autre(s) » ('' : liste vide). Une seule mise en forme
// (12e revue) : « Encore à compléter » (texteACompleter), « Le serveur signale » (bloqueurs du serveur).
export const listeCourte = (prefixe, l) => (l.length
  ? `${prefixe} — ${l.slice(0, 3).join(' · ')}${l.length > 3 ? ` · et ${l.length - 3} autre(s)` : ''}` : '');
// Liste de la note du bouton d'envoi : « Encore à compléter — a · b · c · et N autre(s) » ('' : rien à compléter).
export const texteACompleter = (b) => listeCourte('Encore à compléter', b);
// Note du bouton d'envoi (revue 17/10, 11e passe) — commande de test, sans le droit : ce que l'opérateur ne peut
// pas corriger lui-même est dit UNE fois, par son message — client manquant (`verrouille` : après le 1er mail,
// le même texte que #clientManque : texteClientManqueTest), réf. à corriger (`refBloque`) —, jamais repris dans
// « Encore à compléter » ni avec le texte du serveur « choisissez le client » ; `autres` = ce qui reste à
// compléter, SANS la réf. (sansBloqueursRef).
export function noteEssaiFige({ clientManque = false, verrouille = false, refBloque = false, autres = [] } = {}) {
  return [
    clientManque ? texteClientManqueTest(verrouille) : '',
    refBloque ? MSG_REF_TEST_ADMIN : '',
    texteACompleter(autres),
  ].filter(Boolean).join(' ');
}
// Où mène le bouton d'envoi tant qu'il reste quelque chose à faire : la question d'une fiche,
// puis l'en-tête (réf. de chargement, format), puis le premier champ à saisir, puis la première
// fiche sans photo. Rend l'id à focaliser (ID_NOTE si le blocage n'a pas de champ : refus du serveur…).
// `sansRef` (12e revue) : la réf. est figée pour cette personne (commande de test, sans le droit) — elle est
// sautée, le bouton mène à ce qui reste (jamais au champ désactivé).
export function cibleBloqueur(st, { sansRef = false } = {}) {
  const att = st.units.find((u) => enAttente(st, u));
  if (att) return 'rempl-txt-' + att.id;
  const re = st.refusEntete || {};
  if (!sansRef && (verifRefChargement(st.ord) || re.ord)) return 'ord';
  if (ofType(st, 'PAL').length && (!formatEtat(st).ok || re.fmt)) return st.format === 'AUTRE' ? 'fl' : 'fmt';
  return cibleAllerA(st, st.units);
}

export function counts(st) {
  const pal = ofType(st, 'PAL').length, col = ofType(st, 'COL').length;
  const en = [], fr = [];
  if (pal) { en.push(`${pal} pallet${pal > 1 ? 's' : ''}`); fr.push(`${pal} palette${pal > 1 ? 's' : ''}`); }
  if (col) { en.push(`${col} parcel${col > 1 ? 's' : ''}`); fr.push(`${col} colis`); }
  return { txt: en.join(' and ') || '—', fr: fr.join(' + ') || '—', photos: st.units.reduce((t, u) => t + (u.photos || 0), 0) };
}
export const totalKg = (st) => st.units.reduce((t, u) => t + (num(u.kg) || 0), 0);
export const texteRecap = (st, correction) => {
  const c = counts(st);
  return `${correction ? 'Correction' : 'Envoi'} : ${c.fr}, ${fmtNum(totalKg(st))} kg, ${c.photos} photo${c.photos > 1 ? 's' : ''}.`;
};

// ---------- Nombre d'unités ----------
// Compteur tapé (ou − / +) : plan de ce qu'il faut faire, sans rien modifier.
//   { erreur } — pas un nombre entier ;
//   { cur, n, msg, plafond, demander } — n = nombre visé (ramené au plafond de 60 unités) ;
//   demander = gros ajout (> 10) OU valeur ramenée au plafond (« 45 » tapé → 10 possibles).
export function planCompteur(st, type, wanted, force) {
  const cur = ofType(st, type).length;
  const raw = String(wanted ?? '').trim();
  if (!/^\d+$/.test(raw)) return { erreur: 'Indiquez un nombre entier (0, 1, 2…).', cur };
  let n = Number(raw), msg = '';
  const libre = MAX_UNITS - (st.units.length - cur);
  if (n > libre) { n = libre; msg = `Maximum ${MAX_UNITS} unités par commande.`; }
  const plafond = n !== Number(raw);
  const demander = !force && n > cur && (n - cur > GROS_AJOUT || plafond);
  return { cur, n, msg, plafond, demander };
}
// « − » retire la dernière fiche VIDE de ce type, où qu'elle soit ; jamais une fiche remplie.
// (v2 : une fiche chargée dans un camion n'est jamais vide — le chargement exige son étiquette, et son
// champ SSCC est figé à l'écran — : « vide » suffit, comme au serveur.)
export function videsARetirer(st, type, nombre) {
  const vides = [...ofType(st, type)].reverse().filter(isEmpty);
  return { fiches: vides.slice(0, Math.max(0, nombre)), assez: vides.length >= nombre };
}
export const msgToutesRemplies = (type) => `Toutes les ${type === 'PAL' ? 'palettes' : 'fiches colis'} ont des données : vérifiez laquelle est en trop et supprimez-la avec ✕.`;
export function texteQuestionCompteur(st, a) {
  const cur = ofType(st, a.type).length, mot = a.type === 'PAL' ? 'palette' : 'colis';
  return `Passer de ${cur} à ${a.n} ${mot}${a.type === 'PAL' && a.n > 1 ? 's' : ''} (${a.n - cur} fiches de plus)${a.plafond ? ` — maximum ${MAX_UNITS} unités par commande` : ''} ?`;
}
export const texteQuestionAnnulee = (a) => `Question « ${a.n} ${a.type === 'PAL' ? 'palettes' : 'colis'} » annulée : rien n'a été ajouté.`;
